import React, {FC} from 'react';

import {Note, Text} from '@optimaxdev/design-system';
import {pluralise} from '@optimaxdev/utils';
import classNames from 'classnames';
import AnimateHeight from 'react-animate-height';

import {useCheckIfExamExists} from 'desktop/pages/success/hooks/useCheckIfExamExists';
import {ContactOptionsType, OrderDetailsItemsType} from 'types/orderDetailsTypes';
import {ItemsType} from 'types/successPage';

import {
    getCommonTextProps,
    getNotificationMethod,
    getNotificationText,
    isValidColor,
    NOTIFICATIONS,
} from './helpers';

import s from './contactsItemDetails.scss';

export type PropsType = {
    items: ItemsType;
    item: OrderDetailsItemsType;
    isOpened: boolean;
};

const QuantityBlock: FC<{
    item: OrderDetailsItemsType;
}> = ({item}) => {
    const isDesktop = __BUILD_TYPE__ === 'desktop';

    const {prescription} = item;
    return (
        <div>
            <Text {...getCommonTextProps(isDesktop)} bold>
                Quantity:{' '}
            </Text>
            <Text {...getCommonTextProps(isDesktop)} margin="0" light>
                {item?.subscription?.plan ? 'Subscribe & Save' : 'One-time purchase'}
            </Text>
            <Text {...getCommonTextProps(isDesktop)} margin="0" light>
                {item?.subscription?.plan ? (
                    <>
                        Every {item.subscription.plan.label.toLocaleLowerCase()},{' '}
                        {item.subscription.plan.boxesPerEye}{' '}
                        {pluralise('box', item.subscription.plan.boxesPerEye)} per eye
                    </>
                ) : (
                    <>
                        Right Eye: {prescription?.boxes_od}, Left Eye : {prescription?.boxes_os}
                    </>
                )}
            </Text>
        </div>
    );
};

/**
 * Component details for product item CL TYP
 */
export const ContactsItemDetails: FC<PropsType> = ({isOpened, item, items}) => {
    const options = item.options as ContactOptionsType;
    const {prescription} = item;

    const {examStatusById} = useCheckIfExamExists(item.id);

    const method = getNotificationMethod(item, items, examStatusById);

    const notificationData = NOTIFICATIONS[method];
    const isDesktop = __BUILD_TYPE__ === 'desktop';

    return (
        <AnimateHeight height={isOpened ? 'auto' : 0}>
            <div className={classNames(s.content, {[s.desktop]: isDesktop})}>
                <div>
                    <Text {...getCommonTextProps(isDesktop)} bold>
                        Lens type:{' '}
                    </Text>
                    <Text
                        {...getCommonTextProps(isDesktop)}
                        margin="0"
                        light
                    >{`${options.product.contactLensType}, ${options.product.contactLensPerBox} Pack`}</Text>
                </div>

                {isValidColor(prescription?.color_od) && isValidColor(prescription?.color_os) && (
                    <div>
                        <Text {...getCommonTextProps(isDesktop)} bold>
                            Lens color:{' '}
                        </Text>
                        <Text {...getCommonTextProps(isDesktop)} margin="0" light>
                            {prescription?.color_od === prescription?.color_os
                                ? prescription?.color_od_name
                                : `${prescription?.color_od_name}, ${prescription?.color_os_name}`}
                        </Text>
                    </div>
                )}
                <QuantityBlock item={item} />
                <div>
                    <Text {...getCommonTextProps(isDesktop)} margin="0 0 12px" bold>
                        Prescription filled
                    </Text>
                    {notificationData && (
                        <Note className={s.note} size="regular" type={notificationData.type}>
                            {getNotificationText(method, notificationData.text)}
                        </Note>
                    )}
                </div>
            </div>
        </AnimateHeight>
    );
};
